import DayNames from '@/constants/date/DaysOfWeekString'
import ScheduleTimes from './scheduleTimes'
import ScheduleException from './scheduleException'

/**
 * Represents a complete work schedule with schedule times for each day
 * @param {Object} scheduleTimes - The existing schedule times (e.g., { monday: ScheduleTimes, tuesday: ScheduleTimes, ... })
 * @param {Object} scheduleExceptions - The existing schedule exceptions (e.g., [{ title: 'Holiday', dateLocal: '2024-01-01', id: 1 }, ...])
 */
export default class WorkSchedule {
  constructor({ scheduleTimes = {}, scheduleExceptions = [] } = {}) {
    this.removedDays = _calculateRemovedDays(scheduleTimes) // Pass original scheduleTimes before prefilling
    this.scheduleTimes = _getPrefilledSchedule(scheduleTimes)
    this.scheduleExceptions = scheduleExceptions
      .map((exception) => new ScheduleException(exception))
      .sort((a, b) => (a.dateMeta.isBefore(b.dateMeta) ? -1 : 1))
  }

  /**
   * Updates the removed days
   * @param {string[]} updatedTableItems - Array of table items to mark as removed
   */
  updateRemovedDays(updatedTableItems) {
    const removedDays = updatedTableItems
      .filter((item) => item.removed)
      .map((item) => item.name.toLowerCase())

    this.removedDays = removedDays
  }

  /**
   * Gets a schedule exception by id
   * @param {number} id - The id of the schedule exception to get
   * @returns {ScheduleException|null}
   */
  getException(id) {
    return this.scheduleExceptions.find((e) => e.id === id) || null
  }

  /**
   * Add **or** update.  If `id` exists, it's patched; if not, a new one is pushed.
   * @param {Object} payload – Same shape you pass (title, dateLocal, id)
   * @param {string?} id     – Pass the existing id to patch, or leave empty to add
   */
  upsertException(payload) {
    const id = payload.id || -1
    const existingException = this.getException(id)

    if (!existingException) {
      // Add
      this.scheduleExceptions.push(new ScheduleException(payload))
    } else {
      // Update
      existingException.update(payload)
    }

    this.scheduleExceptions.sort((a, b) =>
      a.dateMeta.isBefore(b.dateMeta) ? -1 : 1
    )
  }

  removeException(id) {
    const idx = this.scheduleExceptions.findIndex((e) => e.id === id)
    if (idx !== -1) {
      this.scheduleExceptions.splice(idx, 1)
    }
  }
}

/**
 * Calculates which days are removed (closed) based on null values in scheduleTimes
 * @param {Object} scheduleTimes - The schedule times to check
 * @returns {string[]} Array of day names that are removed
 */
function _calculateRemovedDays(scheduleTimes) {
  const useDefault = Object.values(scheduleTimes).every((day) => day === null)

  return useDefault
    ? DayNames.slice(-2)
    : DayNames.filter((day) => scheduleTimes[day] === null)
}

/**
 * Prefills the schedule with default times if no times are provided
 * @param {Object} scheduleTimes - The schedule times to prefill
 * @returns {Object} The prefilled schedule times
 */
function _getPrefilledSchedule(scheduleTimes) {
  const prefilledTimes = _getFilledScheduleTimes(scheduleTimes)
  const useDefault = Object.values(prefilledTimes).every((day) => day === null)

  DayNames.forEach((day) => {
    scheduleTimes[day] = useDefault
      ? new ScheduleTimes()
      : new ScheduleTimes(prefilledTimes[day])
  })

  return scheduleTimes
}

/**
 * Fills the schedule with the last valid times for each day
 * @param {Object} scheduleTimes - The schedule times to fill
 * @returns {Object} The filled schedule times
 */
function _getFilledScheduleTimes(scheduleTimes) {
  const firstValidTimes = Object.values(scheduleTimes).find(
    (day) => day !== null
  )

  return DayNames.reduce(
    (acc, day) => {
      const times = scheduleTimes[day]

      acc.lastValidTimes = times || acc.lastValidTimes || firstValidTimes
      acc.schedule[day] = acc.lastValidTimes || null

      return acc
    },
    { schedule: {}, lastValidTimes: null }
  ).schedule
}
